import * as THREE from 'three';
import type { ItemId } from './inventory';
import { makeNormalTexture } from '../engine/textures';
import { applyInteriorDark } from '../engine/interior';
import { preloadGLB, cloneGLBIfReady, onGLBReady, fitGLBTemplate, makeGLBBuildable, fitGLBWallMount } from '../engine/gltf';
import { getPlayerSkin } from './player';

// Craftable DEPLOYABLES: benches, furnaces, storage, doors/windows, traps, etc. Each is a procedural low-poly
// model (primitives) plus a RECIPE (component/resource cost + craft time). buildCraftableModel() returns a
// fresh THREE.Group (origin at base centre, on y=0); the /admin?page=craftables tool renders the grid + shows
// each recipe. Metal uses a shared scratched normal map + rivets/plates so it reads as scavenged, cobbled
// metal rather than flat boxes. Wiring these into the in-game crafting menu + placement comes later.

export type CraftableId =
  | 'baseclaim'
  | 'bench1' | 'bench2' | 'bench3' | 'research_table' | 'repair_table'
  | 'furnace' | 'large_furnace' | 'oil_refinery'
  | 'sleeping_bag' | 'bed' | 'fridge' | 'locker' | 'drop_box'
  | 'wooden_door' | 'metal_door' | 'metal_double_door' | 'reinforced_door'
  | 'wood_window' | 'metal_window' | 'glass_window' | 'reinforced_window'
  | 'water_catcher' | 'large_water_catcher'
  | 'wooden_lock' | 'keypad_lock'
  | 'pistol_trap' | 'shotgun_trap' | 'flame_trap' | 'spikes' | 'mine'
  | 'torch_sconce' | 'shelf' | 'wood_sign' | 'hanging_lantern' // wall/ceiling-mounted furnishings
  | 'planter'; // farming: seeds go in, staged crops grow out (game/farming.ts)

export interface Recipe { items: Array<[ItemId, number]>; seconds: number; }
export interface CraftableDef { id: CraftableId; label: string; recipe: Recipe; }
export const CRAFTABLE_IDS: ReadonlySet<CraftableId> = new Set<CraftableId>(
  ['baseclaim',
   'bench1', 'bench2', 'bench3', 'research_table', 'repair_table', 'furnace', 'large_furnace', 'oil_refinery',
   'sleeping_bag', 'bed', 'fridge', 'locker', 'drop_box', 'wooden_door', 'metal_door', 'metal_double_door', 'reinforced_door', 'wood_window',
   'metal_window', 'glass_window', 'reinforced_window', 'water_catcher', 'large_water_catcher', 'wooden_lock',
   'keypad_lock', 'pistol_trap', 'shotgun_trap', 'flame_trap', 'spikes', 'mine',
   'torch_sconce', 'shelf', 'wood_sign', 'hanging_lantern', 'planter'],
);

export const CRAFTABLES: CraftableDef[] = [
  { id: 'baseclaim', label: 'Base Claim', recipe: { items: [['wood', 1000]], seconds: 30 } }, // THE claim -- makes a base yours
  { id: 'bench1', label: 'Workbench Lvl 1', recipe: { items: [['wood', 500], ['metal', 100], ['salvage', 50]], seconds: 20 } },     // Rust WB1
  { id: 'bench2', label: 'Workbench Lvl 2', recipe: { items: [['metal', 500], ['metal_plating', 20], ['salvage', 250]], seconds: 30 } }, // Rust WB2
  { id: 'bench3', label: 'Workbench Lvl 3', recipe: { items: [['metal', 1000], ['metal_plating', 100], ['electronics', 5]], seconds: 40 } }, // Rust WB3
  { id: 'research_table', label: 'Research Table', recipe: { items: [['wood', 200], ['stone', 75]], seconds: 10 } },
  { id: 'repair_table', label: 'Repair Table', recipe: { items: [['wood', 75], ['metal', 125], ['cogs', 5]], seconds: 8 } },
  { id: 'furnace', label: 'Furnace', recipe: { items: [['stone', 200], ['wood', 100], ['light_fuel', 50]], seconds: 8 } },
  { id: 'large_furnace', label: 'Large Furnace', recipe: { items: [['stone', 500], ['wood', 600], ['light_fuel', 75]], seconds: 20 } },
  { id: 'oil_refinery', label: 'Oil Refinery', recipe: { items: [['metal', 200], ['pipe', 4], ['electronics', 2]], seconds: 12 } },
  { id: 'sleeping_bag', label: 'Sleeping Bag', recipe: { items: [['cloth', 30]], seconds: 4 } },
  { id: 'bed', label: 'Bed', recipe: { items: [['wood', 100], ['cloth', 50]], seconds: 8 } },
  { id: 'fridge', label: 'Fridge', recipe: { items: [['metal', 100], ['electronics', 3], ['cogs', 5]], seconds: 12 } },
  { id: 'locker', label: 'Locker', recipe: { items: [['metal', 150], ['cloth', 10]], seconds: 10 } },
  { id: 'drop_box', label: 'Drop Box', recipe: { items: [['metal', 100]], seconds: 8 } },
  { id: 'wooden_door', label: 'Wooden Door', recipe: { items: [['wood', 100]], seconds: 5 } },
  { id: 'metal_door', label: 'Metal Door', recipe: { items: [['metal', 150]], seconds: 8 } },
  { id: 'metal_double_door', label: 'Metal Double Door', recipe: { items: [['metal', 200]], seconds: 10 } },
  { id: 'reinforced_door', label: 'Reinforced Door', recipe: { items: [['refined_metal', 23], ['cogs', 2]], seconds: 14 } }, // armor-plate door, a tier above metal (~230 metal-equivalent at the 10:1 refinery rate)
  { id: 'wood_window', label: 'Wood Window', recipe: { items: [['wood', 50]], seconds: 3 } },
  { id: 'metal_window', label: 'Metal Window', recipe: { items: [['metal', 75]], seconds: 5 } },
  { id: 'glass_window', label: 'Glass Window', recipe: { items: [['wood', 20], ['salvage', 10]], seconds: 4 } },
  { id: 'reinforced_window', label: 'Reinforced Window', recipe: { items: [['metal', 100], ['salvage', 20]], seconds: 8 } },
  { id: 'water_catcher', label: 'Water Catcher', recipe: { items: [['wood', 10], ['tarp', 1], ['metal', 20]], seconds: 6 } },
  { id: 'large_water_catcher', label: 'Large Water Catcher', recipe: { items: [['metal', 100], ['tarp', 2], ['pipe', 2]], seconds: 12 } },
  { id: 'wooden_lock', label: 'Wooden Lock', recipe: { items: [['wood', 30]], seconds: 3 } },
  { id: 'keypad_lock', label: 'Keypad Lock', recipe: { items: [['metal', 25], ['electronics', 2]], seconds: 6 } },
  { id: 'pistol_trap', label: 'Pistol Trap', recipe: { items: [['metal', 75], ['gun_receiver', 1], ['spring', 2]], seconds: 10 } },
  { id: 'shotgun_trap', label: 'Shotgun Trap', recipe: { items: [['metal', 100], ['gun_receiver', 1], ['spring', 3], ['electronics', 1]], seconds: 12 } },
  { id: 'flame_trap', label: 'Flame Trap', recipe: { items: [['metal', 50], ['light_fuel', 25], ['pipe', 2]], seconds: 10 } },
  { id: 'spikes', label: 'Wooden Spikes', recipe: { items: [['wood', 100]], seconds: 4 } },
  { id: 'mine', label: 'Land Mine', recipe: { items: [['metal', 50], ['sulfur', 30], ['spring', 1]], seconds: 8 } },
  // Wall/ceiling-mounted furnishings (the payoff items for the mount system):
  { id: 'torch_sconce', label: 'Torch Sconce', recipe: { items: [['wood', 40], ['cloth', 5], ['fat', 5]], seconds: 4 } },
  { id: 'shelf', label: 'Shelf', recipe: { items: [['wood', 75], ['metal', 15]], seconds: 6 } },
  { id: 'wood_sign', label: 'Wooden Sign', recipe: { items: [['wood', 30]], seconds: 3 } },
  { id: 'hanging_lantern', label: 'Hanging Lantern', recipe: { items: [['metal', 30], ['fat', 10]], seconds: 5 } },
  { id: 'planter', label: 'Planter', recipe: { items: [['wood', 100]], seconds: 6 } }, // cheap on purpose: farming should be an early food answer
];

// --- materials --------------------------------------------------------------------------------------
// Shared scratched/dented normal map so metal reads as scavenged steel, not a flat colour.
const METAL_N = makeNormalTexture({ size: 256, octaves: 5, period: 9, strength: 1.9, seed: 21 });
METAL_N.repeat.set(2, 2);
// SHARED material cache (Phase 2): every placed deployable reuses ONE instance per (kind, colour, params)
// instead of a fresh set per placement. Nothing here mutates a material after creation, and removals only
// dispose geometry, so sharing is safe.
const MAT_CACHE = new Map<string, THREE.MeshStandardMaterial>();
function cached(key: string, make: () => THREE.MeshStandardMaterial): THREE.MeshStandardMaterial {
  let m = MAT_CACHE.get(key);
  if (!m) {
    m = make();
    // Every deployable joins the sealed-base interior dim here (one choke point for all models):
    // without it, stations/chests OUTSIDE the walls glowed full-bright through the OLOS dark while
    // the organic world faded -- the "doesn't ghost right" the owner reported from inside a base.
    applyInteriorDark(m);
    MAT_CACHE.set(key, m);
  }
  return m;
}
function METAL(c = 0x8a8f96, rough = 0.5): THREE.MeshStandardMaterial {
  // metalness capped in the 0.55-0.75 weathered band (MODELING.md): 0.82 read near-black under the env.
  return cached(`M${c}|${rough}`, () => new THREE.MeshStandardMaterial({ color: c, roughness: rough, metalness: 0.72, normalMap: METAL_N, normalScale: new THREE.Vector2(0.75, 0.75) }));
}
const SCRAP = (c = 0x6b6f76) => METAL(c, 0.74);  // rougher scavenged plate
const DARKM = () => METAL(0x32353a, 0.5);
function mat(c: number, r = 0.7, m = 0.2): THREE.MeshStandardMaterial {
  return cached(`P${c}|${r}|${m}`, () => new THREE.MeshStandardMaterial({ color: c, roughness: r, metalness: m, flatShading: true }));
}
const WOOD = (c = 0x8a6a3f) => mat(c, 0.9, 0.04);
const STONE = (c = 0x7c8088) => mat(c, 0.96, 0.02);
function glow(c: number): THREE.MeshStandardMaterial {
  return cached(`G${c}`, () => {
    const m = new THREE.MeshStandardMaterial({ color: c, roughness: 0.5, metalness: 0, flatShading: true });
    m.emissive = new THREE.Color(c); m.emissiveIntensity = 1.3;
    return m;
  });
}

// --- primitive helpers ------------------------------------------------------------------------------
function box(g: THREE.Group, w: number, h: number, d: number, x: number, y: number, z: number, m: THREE.Material, rotY = 0): void { const e = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m); e.position.set(x, y, z); e.rotation.y = rotY; e.castShadow = true; e.receiveShadow = true; g.add(e); }
function cyl(g: THREE.Group, rt: number, rb: number, h: number, x: number, y: number, z: number, m: THREE.Material, s = 16): void { const e = new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, s), m); e.position.set(x, y, z); e.castShadow = true; e.receiveShadow = true; g.add(e); }
// a cylinder lying along the x or z axis (barrels, bars, rolls)
function tube(g: THREE.Group, r: number, len: number, x: number, y: number, z: number, m: THREE.Material, axis: 'x' | 'z' = 'z', s = 12): void { const e = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, s), m); e.rotation[axis === 'z' ? 'x' : 'z'] = Math.PI / 2; e.position.set(x, y, z); e.castShadow = true; g.add(e); }
function cone(g: THREE.Group, r: number, h: number, x: number, y: number, z: number, m: THREE.Material, s = 6): THREE.Mesh { const e = new THREE.Mesh(new THREE.ConeGeometry(r, h, s), m); e.position.set(x, y, z); e.castShadow = true; g.add(e); return e; }
function rivet(g: THREE.Group, x: number, y: number, z: number, m: THREE.Material = DARKM()): void { const e = new THREE.Mesh(new THREE.SphereGeometry(0.02, 6, 4), m); e.position.set(x, y, z); g.add(e); }
// a riveted metal plate (front face at +z), slightly tilted for a hand-bolted look
function plate(g: THREE.Group, w: number, h: number, x: number, y: number, z: number, m: THREE.Material, tilt = 0): void { box(g, w, h, 0.04, x, y, z, m); for (const sx of [-1, 1]) for (const sy of [-1, 1]) rivet(g, x + sx * (w / 2 - 0.05), y + sy * (h / 2 - 0.05), z + 0.025); if (tilt) (g.children[g.children.length - 5] as THREE.Mesh).rotation.z = tilt; }
function table(c = 0x8a6a3f, top = 0.1, leg = 0.1): THREE.Group { const g = new THREE.Group(); box(g, 1.4, top, 0.7, 0, 0.85, 0, WOOD(c)); for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, leg, 0.8, leg, sx * 0.63, 0.4, sz * 0.29, WOOD(0x5f4827)); return g; }
// the mesh just added by box/cyl/tube, for the occasional post-tweak (a lean/tilt) without extra helper params
function last(g: THREE.Group): THREE.Mesh { return g.children[g.children.length - 1] as THREE.Mesh; }
// Hero deployables (benches, stations, storage, the claim) read a tad small at the iso camera distance
// (owner feedback). One uniform scale-up keeps every proportion AND the base-at-y=0 origin; the FOOT
// half-extents in deployables.ts (and the chest/campfire colliders) carry the same factor.
const HERO_SCALE = 1.15;
function hero(g: THREE.Group): THREE.Group { g.scale.setScalar(HERO_SCALE); return g; }

// Owner-modelled GLBs (public/models/*.glb): fitGLBTemplate + makeGLBBuildable now live in engine/gltf.ts
// (assets.ts's world-scatter barrels need the same "instant procedural placeholder, swap when the GLB
// resolves" pattern, so it moved to the shared loader module instead of living only here).

// --- base claim -------------------------------------------------------------------------------------
// Owner-modelled asset (2026-07-08): a real GLB replacing the procedural locker below (kept as the
// instant-load fallback + the historical baseclaim.png icon reference for its shape).
const BASECLAIM_FILE = 'baseclaim.glb';
preloadGLB('/models/' + BASECLAIM_FILE);
function makeBaseclaim(): THREE.Group { return makeGLBBuildable(BASECLAIM_FILE, 1, buildProceduralBaseclaim); }

// Modelled to the owner's baseclaim.png icon: a brown metal LOCKER -- dark frame, tall vented door on the
// left, a right-hand column of open cubby / drawer / lower door, and a full-width bottom drawer on feet.
// The glowing emblem stays (small, on the centre stile): it anchors the status lamp + lock visuals.
function buildProceduralBaseclaim(g: THREE.Group): void {
  const W = 1.0, H = 1.3, D = 0.55;
  const F = D / 2, PANEL = 0x9a7a4e; // tan face panels on a mid-brown carcass
  box(g, W, H, D, 0, H / 2, 0, WOOD(0x6b4f30));
  box(g, W * 1.08, 0.08, D * 1.08, 0, H + 0.04, 0, DARKM());                      // lid
  box(g, W * 1.04, 0.06, D * 1.04, 0, 0.27, 0, DARKM());                          // rail over the bottom drawer
  for (const sx of [-1, 1]) box(g, 0.07, H, 0.07, sx * W / 2, H / 2, F, DARKM()); // dark front frame edges
  for (const sx of [-1, 1]) box(g, 0.1, 0.08, 0.1, sx * W * 0.4, 0.04, D * 0.28, DARKM()); // feet
  // LEFT: tall door with vent slits + handle.
  box(g, 0.4, 0.9, 0.05, -0.25, 0.77, F + 0.02, WOOD(PANEL));
  for (const vy of [1.06, 0.98, 0.9]) box(g, 0.26, 0.028, 0.06, -0.25, vy, F + 0.03, DARKM()); // vents
  box(g, 0.05, 0.14, 0.06, -0.09, 0.66, F + 0.045, DARKM());                      // handle
  box(g, 0.06, 0.9, 0.06, -0.015, 0.77, F + 0.01, DARKM());                       // centre stile
  // RIGHT column: open cubby / drawer / small door.
  box(g, 0.4, 0.26, 0.04, 0.24, 1.09, F + 0.01, mat(0x241d15, 0.95, 0));          // cubby recess
  box(g, 0.4, 0.05, 0.06, 0.24, 0.945, F + 0.02, DARKM());                        // cubby lip
  box(g, 0.4, 0.24, 0.05, 0.24, 0.78, F + 0.02, WOOD(PANEL));                     // drawer face
  box(g, 0.16, 0.035, 0.06, 0.24, 0.78, F + 0.05, DARKM());                       // drawer handle
  box(g, 0.4, 0.3, 0.05, 0.24, 0.49, F + 0.02, WOOD(0x8a6c42));                   // lower door
  box(g, 0.04, 0.1, 0.06, 0.09, 0.49, F + 0.045, DARKM());                        // its latch
  // BOTTOM: full-width drawer with a wide pull.
  box(g, W * 0.86, 0.16, 0.05, 0, 0.15, F + 0.02, WOOD(PANEL));
  box(g, 0.3, 0.04, 0.06, 0, 0.15, F + 0.05, DARKM());
  box(g, 0.11, 0.11, 0.05, -0.015, H * 0.63, F + 0.04, glow(0x6fe0c0));           // claim emblem badge
  hero(g);
}

// --- workbenches ------------------------------------------------------------------------------------
// Owner-modelled GLBs (2026-07-08) replacing the three procedural benches below (kept as instant-load
// fallbacks).
const BENCH_FILES: readonly [string, string, string] = ['workbench_lvl1.glb', 'workbench_lvl2.glb', 'workbench_lvl3.glb'];
for (const f of BENCH_FILES) preloadGLB('/models/' + f);
function makeBench1(): THREE.Group { return makeGLBBuildable(BENCH_FILES[0], 1, buildProceduralBench1); }
function makeBench2(): THREE.Group { return makeGLBBuildable(BENCH_FILES[1], 1, buildProceduralBench2); }
function makeBench3(): THREE.Group { return makeGLBBuildable(BENCH_FILES[2], 1, buildProceduralBench3); }

function buildProceduralBench1(g: THREE.Group): void {
  g.add(...table().children);
  box(g, 0.22, 0.14, 0.2, -0.4, 0.97, 0, SCRAP()); box(g, 0.05, 0.22, 0.05, 0.3, 1.0, 0.1, WOOD(0x6b5230));
  for (const sx of [-0.45, -0.35]) rivet(g, sx, 0.97, 0.1, DARKM());
  box(g, 1.4, 0.03, 0.05, 0, 0.88, 0.34, WOOD(0x4f3c20));                              // worn front edge strip
  box(g, 0.24, 0.03, 0.06, 0.05, 0.92, 0.2, WOOD(0x8a7448)); box(g, 0.06, 0.06, 0.06, 0.16, 0.94, 0.2, DARKM()); // mallet dropped on the top
  for (const i of [0, 1]) { box(g, 0.09, 0.9, 0.03, -0.52 + i * 0.14, 0.45, 0.38, WOOD(i ? 0x6b5230 : 0x5f4827)); last(g).rotation.x = -0.24; } // spare planks leaning on the front
  hero(g);
}
function buildProceduralBench2(g: THREE.Group): void {
  g.add(...table(0x6b5236).children);
  box(g, 1.44, 0.05, 0.74, 0, 0.92, 0, METAL(0x46627f));
  for (const sx of [-0.6, 0, 0.6]) rivet(g, sx, 0.95, 0.33);
  box(g, 0.26, 0.18, 0.22, -0.35, 1.0, 0, DARKM()); box(g, 0.2, 0.16, 0.2, 0.35, 0.99, -0.08, METAL(0x3a5c84, 0.55));
  box(g, 0.4, 0.32, 0.36, 0.35, 0.16, 0, WOOD(0x5f4827)); box(g, 0.42, 0.04, 0.38, 0.35, 0.34, 0, WOOD(0x4f3c20)); // parts crate stashed under the top
  tube(g, 0.018, 0.26, -0.05, 0.96, 0.16, DARKM(), 'x', 6); box(g, 0.07, 0.03, 0.05, 0.1, 0.96, 0.16, DARKM());   // wrench on the worktop
  hero(g);
}
function buildProceduralBench3(g: THREE.Group): void {
  box(g, 1.55, 0.15, 0.82, 0, 0.86, 0, WOOD(0x6b5236));                              // thick top
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.15, 0.84, 0.15, sx * 0.66, 0.42, sz * 0.32, WOOD(0x5f4827)); // beefy legs
  box(g, 1.34, 0.07, 0.56, 0, 0.36, 0, WOOD(0x4f3c20));                              // shelf between the legs
  box(g, 1.5, 0.66, 0.06, 0, 1.24, -0.38, WOOD(0x5f4827));                           // backboard at the rear
  box(g, 1.48, 0.05, 0.5, 0, 0.95, 0.1, METAL(0x3f7d50));                            // green metal worktop
  for (const sx of [-0.6, 0, 0.6]) rivet(g, sx, 0.98, 0.33);
  box(g, 0.22, 0.18, 0.2, -0.42, 1.04, 0, DARKM());                                  // vise
  for (const px of [-0.4, -0.1, 0.2, 0.5]) tube(g, 0.015, 0.16, px, 1.36, -0.34, DARKM(), 'x', 6); // tools hung on the backboard
  box(g, 0.5, 0.16, 0.04, 0.25, 0.72, 0.4, WOOD(0x4f3c20)); rivet(g, 0.25, 0.72, 0.43); // drawer front under the top + pull
  cyl(g, 0.05, 0.05, 0.16, -0.4, 0.44, 0.1, mat(0x4a5234, 0.6, 0.2), 8);              // oil can on the shelf
  hero(g);
}
// Owner-modelled GLBs (2026-07-09) replacing the procedural research + repair tables below (kept as the
// instant-load fallbacks).
const RESEARCH_TABLE_FILE = 'research_table.glb';
const REPAIR_TABLE_FILE = 'repair_table.glb';
preloadGLB('/models/' + RESEARCH_TABLE_FILE);
preloadGLB('/models/' + REPAIR_TABLE_FILE);
function makeResearch(): THREE.Group { return makeGLBBuildable(RESEARCH_TABLE_FILE, 1, buildProceduralResearch); }
function makeRepair(): THREE.Group { return makeGLBBuildable(REPAIR_TABLE_FILE, 1, buildProceduralRepair); }

function buildProceduralResearch(g: THREE.Group): void {
  g.add(...table(0x7a5f3e).children);
  const blue = mat(0x2f5a9a, 0.6, 0); // blueprint sheets fanned across the top
  box(g, 0.5, 0.012, 0.36, -0.1, 0.915, 0.02, blue, 0.1);
  box(g, 0.46, 0.012, 0.32, 0.18, 0.92, -0.06, mat(0x2a4f8a, 0.6, 0), -0.18);
  box(g, 0.42, 0.012, 0.3, 0.0, 0.928, 0.12, mat(0x356aa8, 0.6, 0), 0.05);
  box(g, 0.04, 0.04, 0.04, 0.42, 0.93, 0.2, SCRAP()); // weight holding the corner down
  tube(g, 0.03, 0.34, -0.45, 0.945, -0.18, mat(0xd8cfba, 0.85, 0), 'x', 8);           // a rolled blueprint waiting its turn
  cyl(g, 0.035, 0.04, 0.07, 0.5, 0.945, -0.15, mat(0x22262e, 0.5, 0.1), 8);           // ink pot
  box(g, 1.4, 0.03, 0.05, 0, 0.88, 0.34, WOOD(0x5f4827));                             // worn front edge strip
  hero(g);
}
function buildProceduralRepair(g: THREE.Group): void {
  box(g, 1.3, 0.1, 0.66, 0, 0.84, 0, SCRAP(0x5e636b));                               // riveted metal top
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) { box(g, 0.12, 0.78, 0.12, sx * 0.55, 0.4, sz * 0.26, METAL(0x55595f)); rivet(g, sx * 0.55, 0.8, sz * 0.26 + (sz > 0 ? 0.06 : -0.06)); }
  for (const sx of [-0.5, 0, 0.5]) rivet(g, sx, 0.89, 0.3);
  box(g, 0.28, 0.14, 0.16, 0.1, 0.96, 0, DARKM()); box(g, 0.46, 0.07, 0.2, 0.1, 0.92, 0, METAL(0x6b6f76)); // anvil
  box(g, 0.18, 0.16, 0.18, -0.42, 0.97, 0, DARKM()); // vise/clamp
  const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.05, 12), STONE(0x8a8f96)); // side grinding wheel
  wheel.rotation.z = Math.PI / 2; wheel.position.set(0.68, 0.92, 0); wheel.castShadow = true; g.add(wheel);
  for (const px of [-0.2, 0.05]) tube(g, 0.014, 0.2, px, 0.68, 0.34, DARKM(), 'x', 6); // tools hung off the front rail
  box(g, 0.36, 0.26, 0.3, -0.35, 0.14, 0, SCRAP(0x4a4d52));                            // scrap bin underneath
  hero(g);
}

// --- furnaces / refinery ----------------------------------------------------------------------------
// Owner-modelled GLBs (2026-07-08): TWO variants -- furnace_on.glb (glowing, shown while the furnace is
// actively burning) and furnace_off.glb (cold). Both are added to the group and tagged on g.userData;
// deployables.ts toggles which is visible + drives a SpotLight out of the opening by the furnace's LIT
// state. Both fit like every buildable (recentre + rest on y=0 + scale from buildable_resizers.json --
// F4 -> OBJECTS tunes each). The procedural furnace stays as the instant placeholder while they load.
const FURNACE_ON_FILE = 'furnace_on.glb', FURNACE_OFF_FILE = 'furnace_off.glb';
const LARGE_FURNACE_ON_FILE = 'large_furnace_on.glb', LARGE_FURNACE_OFF_FILE = 'large_furnace_off.glb';
for (const f of [FURNACE_ON_FILE, FURNACE_OFF_FILE, LARGE_FURNACE_ON_FILE, LARGE_FURNACE_OFF_FILE]) preloadGLB('/models/' + f);

function makeFurnace(): THREE.Group { return makeFurnaceLike(FURNACE_ON_FILE, FURNACE_OFF_FILE, buildProceduralFurnace); }
function makeLargeFurnace(): THREE.Group { return makeFurnaceLike(LARGE_FURNACE_ON_FILE, LARGE_FURNACE_OFF_FILE, buildProceduralLargeFurnace); }

// Both furnaces are two GLB variants (on/off) tagged on g.userData for deployables.ts to swap by lit state,
// with the procedural model as the instant placeholder while the pair loads.
function makeFurnaceLike(onFile: string, offFile: string, buildPlaceholder: () => THREE.Group): THREE.Group {
  const g = new THREE.Group();
  const placeholder = buildPlaceholder();
  g.add(placeholder);
  let landed = 0;
  const onLand = (): void => { if (++landed >= 2 && placeholder.parent) { g.remove(placeholder); placeholder.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); }); } };
  loadFurnaceVariant(g, offFile, 'furnaceOff', true, onLand);  // cold model shown by default
  loadFurnaceVariant(g, onFile, 'furnaceOn', false, onLand);   // glowing model hidden until lit
  return g;
}
function loadFurnaceVariant(g: THREE.Group, file: string, tag: 'furnaceOn' | 'furnaceOff', visible: boolean, onLand: () => void): void {
  const url = '/models/' + file;
  const place = (clone: THREE.Object3D): void => {
    const fitted = fitGLBTemplate(clone, file, 1);
    fitted.visible = visible;
    (g.userData as Record<string, unknown>)[tag] = fitted; // deployables.ts reads these refs to swap by lit
    g.add(fitted);
    onLand();
  };
  const ready = cloneGLBIfReady(url);
  if (ready) place(ready); else onGLBReady(url, place);
}
function buildProceduralFurnace(): THREE.Group {
  const g = new THREE.Group();
  // rough stacked-stone body: a few jittered blocks rather than one clean box
  box(g, 0.74, 0.34, 0.62, 0.02, 0.2, 0, STONE(0x767a80), 0.05);
  box(g, 0.7, 0.32, 0.58, -0.02, 0.52, 0.02, STONE(0x6e7178), -0.06);
  box(g, 0.64, 0.26, 0.54, 0.03, 0.78, -0.02, STONE(0x7c8088), 0.04);
  box(g, 0.36, 0.34, 0.05, 0, 0.32, 0.31, DARKM());                                  // mouth
  box(g, 0.28, 0.26, 0.04, 0, 0.32, 0.32, glow(0xff7a1a));
  plate(g, 0.3, 0.34, -0.28, 0.55, 0.3, SCRAP(0x595d64), 0.12);                       // bolted-on scrap patch
  cyl(g, 0.08, 0.11, 0.34, 0.24, 1.05, -0.12, STONE(0x6e7178), 8);                    // chimney (leaning)
  last(g).rotation.z = 0.12;
  box(g, 0.4, 0.02, 0.3, 0, 0.012, 0.42, mat(0x211d18, 0.98, 0));                     // scorch plate under the fire mouth
  tube(g, 0.016, 0.62, 0.34, 0.32, 0.28, DARKM(), 'z', 6); last(g).rotation.x = 1.1;  // poker leaning on the mouth
  for (const [rx, rz] of [[-0.34, 0.3], [0.4, 0.22]] as const) box(g, 0.12, 0.09, 0.1, rx, 0.05, rz, STONE(0x6a6d74), rx); // spilled rubble at the base
  return hero(g);
}
function buildProceduralLargeFurnace(): THREE.Group {
  const g = new THREE.Group();
  box(g, 1.2, 1.0, 0.8, 0, 0.52, 0, STONE());
  for (const sx of [-0.3, 0.3]) { box(g, 0.3, 0.3, 0.05, sx, 0.4, 0.4, DARKM()); box(g, 0.24, 0.22, 0.04, sx, 0.4, 0.41, glow(0xff7a1a)); }
  plate(g, 0.4, 0.4, 0, 0.75, 0.41, SCRAP(0x595d64), 0.08);
  cyl(g, 0.1, 0.12, 0.4, 0.4, 1.2, -0.2, STONE(), 8);
  for (const sx of [-0.3, 0.3]) box(g, 0.34, 0.02, 0.28, sx, 0.012, 0.5, mat(0x211d18, 0.98, 0)); // scorch plates under both mouths
  for (const sx of [-1, 1]) box(g, 0.08, 1.04, 0.08, sx * 0.56, 0.52, 0.38, DARKM()); // iron corner straps
  for (const i of [0, 1]) tube(g, 0.06, 0.5, 0.72, 0.07 + i * 0.11, -0.1 + i * 0.04, WOOD(0x5a3b22), 'z', 6); // fuel logs stacked at the side
  return hero(g);
}
// Owner-modelled GLB (2026-07-09) replacing the procedural refinery below (kept as the instant-load fallback).
const OIL_REFINERY_FILE = 'oil_refinery.glb';
preloadGLB('/models/' + OIL_REFINERY_FILE);
function makeRefinery(): THREE.Group { return makeGLBBuildable(OIL_REFINERY_FILE, 1, buildProceduralRefinery); }
function buildProceduralRefinery(g: THREE.Group): void {
  cyl(g, 0.3, 0.3, 0.9, 0, 0.5, 0, SCRAP(0x6f7378), 18); cyl(g, 0.32, 0.32, 0.05, 0, 0.95, 0, DARKM(), 18);
  for (const a of [0, 1.6, 3.1, 4.7]) rivet(g, Math.cos(a) * 0.31, 0.7, Math.sin(a) * 0.31);
  box(g, 0.34, 0.6, 0.34, 0.42, 0.32, 0.1, mat(0x4a5234, 0.6, 0.3)); // fuel barrel
  tube(g, 0.04, 0.5, 0.2, 0.7, 0, DARKM(), 'x', 8); box(g, 0.2, 0.3, 0.05, 0, 0.3, 0.31, glow(0xff7a1a));
  const valve = new THREE.Mesh(new THREE.TorusGeometry(0.07, 0.016, 6, 12), mat(0xa03a2a, 0.6, 0.3)); // shutoff valve wheel
  valve.position.set(0, 0.62, 0.31); valve.castShadow = true; g.add(valve);
  cyl(g, 0.045, 0.045, 0.5, -0.2, 1.1, -0.12, DARKM(), 8); tube(g, 0.045, 0.16, -0.2, 1.33, -0.05, DARKM(), 'z', 8); // exhaust stack + elbow
  box(g, 0.4, 0.03, 0.4, 0.42, 0.015, 0.1, mat(0x2a251e, 0.98, 0));  // oil-stained drip tray under the barrel
  box(g, 0.26, 0.02, 0.2, 0, 0.012, 0.4, mat(0x211d18, 0.98, 0));    // scorch under the burner mouth
  hero(g);
}

// --- bedding / storage ------------------------------------------------------------------------------
// Owner-modelled GLBs (2026-07-10) replacing the procedural sleeping bag below (kept as the instant-load
// fallback). 5 colour variants: the bag matches the placer's SHIRT colour when the shirt reads as one of
// them, else a random colour per placement (owner ask). The pick happens per-build (each placement calls
// makeSleepingBag fresh). NOTE: colour is chosen on the local client only -- remotes rebuild the model with
// their own shirt, so bag colour is not yet MP-consistent; syncing it needs a variant field on the deploy op.
const SLEEPING_BAG_GLB = {
  black: 'sleeping_bag_black.glb', blue: 'sleeping_bag_blue.glb', green: 'sleeping_bag_green.glb',
  red: 'sleeping_bag_red.glb', yellow: 'sleeping_bag_yellow.glb',
} as const;
for (const f of Object.values(SLEEPING_BAG_GLB)) preloadGLB('/models/' + f);

function sleepingBagFile(): string {
  const c = getPlayerSkin().shirt;
  const r = (c >> 16) & 255, g = (c >> 8) & 255, b = c & 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  if (mx < 48) return SLEEPING_BAG_GLB.black;                       // near-black shirt
  if (mx - mn >= 28) {                                              // saturated enough to read as a colour (not grey)
    if (r > mx * 0.7 && g > mx * 0.6 && b < g * 0.6) return SLEEPING_BAG_GLB.yellow; // red+green together = yellow
    if (r >= g && r >= b) return SLEEPING_BAG_GLB.red;
    if (g >= r && g >= b) return SLEEPING_BAG_GLB.green;
    return SLEEPING_BAG_GLB.blue;
  }
  const all = Object.values(SLEEPING_BAG_GLB);
  return all[Math.floor(Math.random() * all.length)];               // grey/unmatched shirt: random per placement
}
function makeSleepingBag(): THREE.Group { return makeGLBBuildable(sleepingBagFile(), 1, buildProceduralSleepingBag); }

function buildProceduralSleepingBag(g: THREE.Group): void {
  const leather = mat(0x4a3527, 0.85, 0.02), trim = mat(0x2c2018, 0.8, 0.02);
  box(g, 0.78, 0.05, 1.9, 0, 0.03, 0, trim);                                          // thin base/edge
  for (const x of [-0.24, 0, 0.24]) tube(g, 0.12, 1.72, x, 0.12, 0.06, leather, 'z', 12); // 3 padded quilted tubes
  tube(g, 0.13, 0.78, 0, 0.16, -0.88, mat(0xb89a6a, 0.85, 0), 'x', 12);               // rolled pillow at the head
  for (const z of [-0.4, 0.1, 0.6]) box(g, 0.74, 0.02, 0.03, 0, 0.2, z, trim);        // cross stitching seams
}
// Owner-modelled GLB (2026-07-08) replacing the procedural bed below (kept as the instant-load fallback).
const BED_FILE = 'bed.glb';
preloadGLB('/models/' + BED_FILE);
function makeBed(): THREE.Group { return makeGLBBuildable(BED_FILE, 1, buildProceduralBed); }

function buildProceduralBed(g: THREE.Group): void {
  box(g, 0.9, 0.18, 2.0, 0, 0.16, 0, WOOD(0x6b5236)); box(g, 0.86, 0.16, 1.9, 0, 0.32, 0, mat(0xcdc3b0, 0.9, 0));
  for (const z of [-0.5, 0.0, 0.5]) box(g, 0.84, 0.02, 0.04, 0, 0.41, z, mat(0xb8ad98, 0.9, 0)); // quilt seams
  box(g, 0.8, 0.14, 0.4, 0, 0.45, -0.7, mat(0xeae3d5, 0.9, 0)); box(g, 0.9, 0.5, 0.1, 0, 0.4, -1.0, WOOD(0x5f4827)); // pillow + headboard
}
// Owner-modelled GLB (2026-07-09) replacing the procedural fridge below (kept as the instant-load fallback).
const FRIDGE_FILE = 'fridge.glb';
preloadGLB('/models/' + FRIDGE_FILE);
function makeFridge(): THREE.Group { return makeGLBBuildable(FRIDGE_FILE, 1, buildProceduralFridge); }

function buildProceduralFridge(g: THREE.Group): void {
  box(g, 0.7, 1.5, 0.7, 0, 0.77, 0, METAL(0xb6bcc4, 0.42));
  box(g, 0.66, 0.03, 0.02, 0, 0.95, 0.36, DARKM()); tube(g, 0.025, 0.4, 0.27, 0.9, 0.37, DARKM(), 'x', 8);
  plate(g, 0.5, 0.5, 0, 1.2, 0.36, SCRAP(0x9aa0a8), 0.04);
  box(g, 0.72, 0.1, 0.72, 0, 0.05, 0, SCRAP(0x74787e));                                // dented kick plate
  for (const sx of [-1, 1]) box(g, 0.03, 1.4, 0.03, sx * 0.34, 0.77, 0.355, mat(0x6e4426, 0.92, 0.1)); // rust runs down the door edges
  box(g, 0.2, 0.14, 0.2, 0.1, 1.59, -0.1, WOOD(0x5f4827));                             // junk box dumped on top
  hero(g);
}
// Owner-modelled GLB (2026-07-08) replacing the procedural locker below (kept as the instant-load fallback).
const LOCKER_FILE = 'locker.glb';
preloadGLB('/models/' + LOCKER_FILE);
function makeLocker(): THREE.Group { return makeGLBBuildable(LOCKER_FILE, 1, buildProceduralLocker); }

function buildProceduralLocker(g: THREE.Group): void {
  box(g, 0.6, 1.6, 0.5, 0, 0.82, 0, METAL(0x6f7378, 0.55));
  for (const sy of [-1, 1]) for (const sx of [-1, 1]) rivet(g, sx * 0.26, 0.82 + sy * 0.72, 0.26);
  box(g, 0.05, 0.3, 0.04, 0.22, 0.9, 0.26, DARKM());
  for (const yy of [1.32, 1.42, 1.52]) box(g, 0.3, 0.02, 0.02, 0, yy, 0.26, DARKM());
  box(g, 0.62, 0.08, 0.52, 0, 0.04, 0, SCRAP(0x55595f));                               // kick plate
  box(g, 0.2, 0.08, 0.02, -0.12, 1.24, 0.26, mat(0xa8adb4, 0.7, 0.3));                 // stencil name plate
  box(g, 0.07, 0.1, 0.05, 0.22, 0.62, 0.26, DARKM()); rivet(g, 0.22, 0.58, 0.3);       // padlock hasp under the handle
  hero(g);
}
function makeDropBox(): THREE.Group { const g = new THREE.Group(); box(g, 0.6, 0.6, 0.4, 0, 0.5, 0, METAL(0x6f7378, 0.55)); box(g, 0.42, 0.07, 0.06, 0, 0.7, 0.21, DARKM()); for (const sx of [-1, 1]) for (const sy of [-1, 1]) rivet(g, sx * 0.26, 0.5 + sy * 0.26, 0.21); box(g, 0.5, 0.1, 0.5, 0, 0.95, 0, WOOD(0x5f4827)); return g; }

// --- doors / windows --------------------------------------------------------------------------------
// Crafted door ids. The LEAF visual below is shared between the catalog model (hotbar/admin grid,
// door-sized) and the leaf building.ts fits into a doorway socket, so what you hold is what swings
// in the wall. Every leaf is a THIN ~0.05 slab on purpose: it should read sunken inside the 0.2m
// doorway, not flush with the wall faces.
export type CraftedDoorId = 'wooden_door' | 'metal_door' | 'metal_double_door' | 'reinforced_door';

// A door leaf CENTRED on x=0, base at y=0. building.ts offsets/mirrors it onto the hinge.
export function buildDoorLeafModel(kind: CraftedDoorId, w: number, h: number): THREE.Group {
  if (kind === 'wooden_door') return birchLeaf(w, h);
  if (kind === 'reinforced_door') return armorLeaf(w, h);
  return patchworkLeaf(w, h, kind === 'metal_double_door' ? 1 : 0);
}

// BIRCH: pale planks, dark horizontal lenticel flecks, a lighter frame. The old brown slab read like
// the metal door at a glance; birch keeps the wood tier unmistakably WOOD.
function birchLeaf(w: number, h: number): THREE.Group {
  const g = new THREE.Group();
  box(g, w, h, 0.05, 0, h / 2, 0, mat(0xd3c9b2, 0.85, 0.02));                                          // pale slab
  for (const i of [-1, 0, 1]) box(g, w * 0.29, h * 0.93, 0.012, i * w * 0.315, h / 2, 0.028, mat(i ? 0xcabfa6 : 0xdad1bc, 0.85, 0.02)); // plank faces, alternating pale tints
  const FLECKS: Array<[number, number, number]> = [                                                    // birch-bark streaks: [x frac, y frac, len frac]
    [-0.3, 0.2, 0.10], [0.05, 0.32, 0.16], [0.28, 0.14, 0.08], [-0.12, 0.52, 0.12],
    [0.33, 0.6, 0.10], [-0.28, 0.72, 0.14], [0.1, 0.84, 0.09], [-0.05, 0.08, 0.11],
  ];
  for (const [fx, fy, fw] of FLECKS) box(g, w * fw, 0.022, 0.012, fx * w, h * fy + 0.05, 0.037, mat(0x4a4036, 0.9, 0));
  const frame = mat(0xe4dcc8, 0.8, 0.02); // frame rails a shade LIGHTER than the planks
  for (const yy of [0.04, h - 0.04]) box(g, w, 0.08, 0.056, 0, yy, 0, frame);
  for (const sx of [-1, 1]) box(g, 0.08, h, 0.056, sx * (w / 2 - 0.04), h / 2, 0, frame);
  for (const face of [1, -1] as const) box(g, 0.07, 0.2, 0.05, w * 0.36, h * 0.48, face * 0.04, DARKM()); // handles both faces
  return g;
}

// PATCHWORK: a scavenged sheet-metal door (the Rust look) -- one base plate under mismatched bolted
// patches (a corrugated strip, plain plates, scrap wood slats), rust streaks and heavy rivet rows.
// Deliberately messier than the clean corrugated metal WALL tier so a door never vanishes into it.
function patchworkLeaf(w: number, h: number, variant: number): THREE.Group {
  const g = new THREE.Group();
  const sv = variant ? -1 : 1; // flipped layout so double-door leaves don't twin exactly
  box(g, w, h, 0.05, 0, h / 2, 0, SCRAP(0x66635c));                                                    // scuffed base plate
  box(g, w * 0.34, h * 0.42, 0.02, sv * w * 0.22, h * 0.66, 0.03, METAL(0x757a80, 0.6));               // corrugated strip patch
  for (const i of [-1.5, -0.5, 0.5, 1.5]) box(g, 0.02, h * 0.4, 0.014, sv * w * 0.22 + i * w * 0.075, h * 0.66, 0.045, METAL(0x8a9096, 0.55)); // its ridges
  plate(g, w * 0.36, h * 0.26, -sv * w * 0.2, h * 0.33, 0.03, SCRAP(0x6e6a5f), 0.05);                  // plain plate patch
  plate(g, w * 0.28, h * 0.18, sv * w * 0.16, h * 0.14, 0.03, mat(0x6e4426, 0.92, 0.12), -0.06);       // rusted-through patch
  for (const i of [0, 1]) { box(g, w * 0.34, 0.075, 0.018, -sv * w * 0.18, h * (0.8 + i * 0.09), 0.032, WOOD(0x6b5230)); last(g).rotation.z = i ? -0.08 : 0.1; } // scrap wood slats
  for (const [rx, ry, rh] of [[0.34, 0.55, 0.3], [-0.06, 0.44, 0.22], [0.15, 0.06, 0.16]] as const) box(g, 0.028, h * rh, 0.008, sv * rx * w, h * (ry + rh / 2), 0.038, mat(0x5c3a20, 0.95, 0.05)); // rust bleeding down
  for (const sx of [-0.36, -0.12, 0.12, 0.36]) { rivet(g, sx * w, h - 0.07, 0.035); rivet(g, sx * w, 0.07, 0.035); } // heavy edge rivet rows
  for (const face of [1, -1] as const) box(g, 0.07, 0.2, 0.05, w * 0.36, h * 0.48, face * 0.04, DARKM()); // handles
  return g;
}

// ARMOR: the reinforced door -- machined 2x2 plates, countersunk bolts, a view slit. Clean and flat
// so it reads a clear tier ABOVE the scavenged patchwork metal door.
function armorLeaf(w: number, h: number): THREE.Group {
  const g = new THREE.Group();
  box(g, w, h, 0.05, 0, h / 2, 0, METAL(0x9aa2ac, 0.42));                                              // cool bright steel slab
  for (const sx of [-1, 1]) for (const sy of [0, 1]) {
    box(g, w * 0.44, h * 0.44, 0.014, sx * w * 0.235, h * (sy ? 0.725 : 0.275), 0.028, (sx > 0) === (sy > 0) ? METAL(0x8e96a1, 0.38) : METAL(0xa2aab4, 0.4)); // machined plates, alternating batch tint
    for (const cx of [-1, 1]) for (const cy of [-1, 1]) rivet(g, sx * w * 0.235 + cx * w * 0.17, h * (sy ? 0.725 : 0.275) + cy * h * 0.17, 0.038); // countersunk bolts
  }
  box(g, 0.36, 0.1, 0.02, 0, h * 0.72, 0.032, DARKM());                                                // view-slit bezel
  box(g, 0.28, 0.04, 0.02, 0, h * 0.72, 0.04, mat(0x101216, 0.9, 0.2));                                // the slit itself
  for (const yy of [0.05, h - 0.05]) box(g, w * 1.01, 0.09, 0.058, 0, yy, 0, DARKM());                 // heavy edge bands
  for (const face of [1, -1] as const) box(g, 0.08, 0.22, 0.05, w * 0.36, h * 0.48, face * 0.042, DARKM()); // handles
  return g;
}

function makeWoodDoor(): THREE.Group { return buildDoorLeafModel('wooden_door', 0.95, 2.0); }
function makeMetalDoor(): THREE.Group { return buildDoorLeafModel('metal_door', 0.95, 2.0); }
function makeReinforcedDoor(): THREE.Group { return buildDoorLeafModel('reinforced_door', 0.95, 2.0); }
function makeMetalDoubleDoor(): THREE.Group {
  const g = new THREE.Group();
  for (const s of [-1, 1] as const) {
    const leaf = buildDoorLeafModel('metal_double_door', 0.92, 2.0);
    leaf.position.x = s * 0.46;
    if (s > 0) leaf.scale.x = -1; // mirror the right leaf (negative determinant flips frontFace in the renderer, so faces stay correct)
    g.add(leaf);
  }
  box(g, 0.03, 1.96, 0.056, 0, 1.0, 0, DARKM()); // centre seam cover strip (cosmetic, not a gap)
  return g;
}
// frame + optional pane + bar grid (rods, gaps too small to jump through)
function windowFrame(frameMat: THREE.Material, pane: THREE.Material | null, barMat: THREE.Material | null): THREE.Group {
  const g = new THREE.Group();
  box(g, 1.0, 0.12, 0.14, 0, 1.45, 0, frameMat); box(g, 1.0, 0.12, 0.14, 0, 0.55, 0, frameMat);
  box(g, 0.12, 0.9, 0.14, -0.44, 1.0, 0, frameMat); box(g, 0.12, 0.9, 0.14, 0.44, 1.0, 0, frameMat);
  if (pane) box(g, 0.78, 0.78, 0.04, 0, 1.0, 0, pane);
  if (barMat) { for (const x of [-0.28, -0.09, 0.09, 0.28]) cyl(g, 0.025, 0.025, 0.84, x, 1.0, 0, barMat, 8); for (const y of [0.74, 1.0, 1.26]) tube(g, 0.025, 0.82, 0, y, 0, barMat, 'x', 8); }
  return g;
}
function makeWoodWindow(): THREE.Group { return windowFrame(WOOD(0x6b5236), null, WOOD(0x5a4526)); } // now barred
function makeMetalWindow(): THREE.Group { return windowFrame(METAL(0x6f7378, 0.55), null, DARKM()); } // dense grid
function makeGlassWindow(): THREE.Group {
  const glass = new THREE.MeshStandardMaterial({ color: 0x9fd0e0, roughness: 0.08, metalness: 0, transparent: true, opacity: 0.38 });
  applyInteriorDark(glass); // panes dim with the rest of the world (they sit in walls, often in view)
  return windowFrame(METAL(0x7c8086, 0.5), glass, null);
}
function makeReinforcedWindow(): THREE.Group { const g = windowFrame(METAL(0x5a5e64, 0.5), null, DARKM()); plate(g, 0.24, 0.84, -0.3, 1.0, 0.08, SCRAP(0x646870), 0.05); plate(g, 0.24, 0.84, 0.3, 1.0, 0.08, SCRAP(0x5c6068), -0.04); return g; } // plates cover the sides, ~50% centre open to shoot through

// --- water / locks ----------------------------------------------------------------------------------
// Owner-modelled asset (2026-07-08): a tarp funnel lashed to a barrel with a wood prop stick, replacing
// the procedural funnel-on-legs below (kept as the instant-load fallback). 0.57 was the original
// hand-derived fallback (barrel-to-funnel-rim height vs. the old procedural catcher's 1.05m); the owner
// has since re-dialled it to 1.175 via F4 -> OBJECTS -> Buildables (buildable_resizers.json).
const WATER_CATCHER_FILE = 'rainwater_catcher_v2.glb';
preloadGLB('/models/' + WATER_CATCHER_FILE);
function makeWaterCatcher(): THREE.Group { return makeGLBBuildable(WATER_CATCHER_FILE, 0.57, buildProceduralWaterCatcher); }

function buildProceduralWaterCatcher(g: THREE.Group): void {
  for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) box(g, 0.06, 0.6, 0.06, sx * 0.3, 0.3, sz * 0.3, WOOD(0x6b5236));
  cyl(g, 0.5, 0.1, 0.4, 0, 0.85, 0, mat(0x5f7d6a, 0.7, 0.1), 12);
  cyl(g, 0.25, 0.25, 0.3, 0, 0.45, 0, SCRAP(0x6f7378), 16);
}
// Owner-modelled GLB (2026-07-09) replacing the procedural large water catcher below (kept as the
// instant-load fallback).
const LARGE_WATER_CATCHER_FILE = 'large_rain_catcher.glb';
preloadGLB('/models/' + LARGE_WATER_CATCHER_FILE);
function makeLargeWaterCatcher(): THREE.Group { return makeGLBBuildable(LARGE_WATER_CATCHER_FILE, 1, buildProceduralLargeWaterCatcher); }
function buildProceduralLargeWaterCatcher(g: THREE.Group): void { cyl(g, 0.6, 0.6, 0.9, 0, 0.5, 0, SCRAP(0x5f6368), 18); cyl(g, 0.62, 0.62, 0.06, 0, 0.95, 0, DARKM(), 18); for (const a of [0, 1.6, 3.1, 4.7]) rivet(g, Math.cos(a) * 0.61, 0.7, Math.sin(a) * 0.61); cone(g, 0.7, 0.25, 0, 1.12, 0, mat(0x5f7d6a, 0.7, 0.1), 14); }
function makeWoodenLock(): THREE.Group { const g = new THREE.Group(); box(g, 0.2, 0.3, 0.08, 0, 0.5, 0, WOOD(0x6b5236)); box(g, 0.12, 0.14, 0.06, 0, 0.5, 0.05, DARKM()); rivet(g, 0, 0.5, 0.09); return g; }
function makeKeypadLock(): THREE.Group { const g = new THREE.Group(); box(g, 0.2, 0.28, 0.08, 0, 0.5, 0, METAL(0x6f7378, 0.5)); for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) box(g, 0.03, 0.03, 0.02, (c - 1) * 0.05, 0.5 + (1 - r) * 0.06, 0.05, glow(0x3a8fbf)); return g; }

// --- traps ------------------------------------------------------------------------------------------
// Owner-modelled GLBs (2026-07-11); procedural stays as the instant-load fallback.
const PISTOL_TRAP_FILE = 'trap_pistol.glb';
preloadGLB('/models/' + PISTOL_TRAP_FILE);
function buildProceduralPistolTrap(g: THREE.Group): void { cyl(g, 0.06, 0.08, 0.9, 0, 0.45, 0, METAL(0x6f7378, 0.55), 10); box(g, 0.1, 0.16, 0.28, 0, 0.95, 0.05, DARKM()); tube(g, 0.022, 0.22, 0, 0.99, 0.22, DARKM(), 'z', 8); }
function makePistolTrap(): THREE.Group { return makeGLBBuildable(PISTOL_TRAP_FILE, 0.9, buildProceduralPistolTrap); }
const SHOTGUN_TRAP_FILE = 'trap_shotgun.glb';
preloadGLB('/models/' + SHOTGUN_TRAP_FILE);
function makeShotgunTrap(): THREE.Group { return makeGLBBuildable(SHOTGUN_TRAP_FILE, 0.9, (gg) => { gg.add(buildProceduralShotgunTrap()); }); }
function buildProceduralShotgunTrap(): THREE.Group {
  const g = new THREE.Group();
  cyl(g, 0.06, 0.09, 0.78, 0, 0.39, 0, METAL(0x5f6368, 0.6), 10);                     // mounting post
  box(g, 0.12, 0.06, 0.1, 0, 0.78, 0.02, DARKM());                                    // mount clamp
  // pump shotgun, pointing +z
  const blk = DARKM(), steel = SCRAP(0x4a4d52);
  box(g, 0.07, 0.11, 0.32, 0, 0.86, -0.02, blk);                                      // receiver
  tube(g, 0.024, 0.62, 0, 0.89, 0.42, steel, 'z', 10);                                // barrel
  tube(g, 0.02, 0.5, 0, 0.84, 0.36, blk, 'z', 10);                                     // magazine tube under barrel
  box(g, 0.055, 0.07, 0.16, 0, 0.83, 0.18, mat(0x26282c, 0.6, 0.5));                  // pump/forend
  const grip = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.2, 0.08), blk); grip.position.set(0, 0.78, -0.2); grip.rotation.x = -0.5; grip.castShadow = true; g.add(grip); // pistol grip stock
  return g;
}
// Owner-modelled GLB (2026-07-10) replacing the procedural flame trap below (kept as the instant-load fallback).
const FLAME_TURRET_FILE = 'flame_turret.glb';
preloadGLB('/models/' + FLAME_TURRET_FILE);
function makeFlameTrap(): THREE.Group { return makeGLBBuildable(FLAME_TURRET_FILE, 1, buildProceduralFlameTrap); }
function buildProceduralFlameTrap(g: THREE.Group): void { box(g, 0.3, 0.25, 0.3, 0, 0.2, 0, SCRAP(0x5a5e64)); cyl(g, 0.12, 0.12, 0.5, 0, 0.5, 0, mat(0xc24a3a, 0.6, 0.3), 12); tube(g, 0.04, 0.2, 0, 0.4, 0.25, DARKM(), 'z', 8); box(g, 0.06, 0.06, 0.06, 0, 0.42, 0.36, glow(0xff7a1a)); }
function makeSpikes(): THREE.Group {
  const g = new THREE.Group(); // no base -- a cluster of sharpened stakes splaying UP + OUTWARD in all directions
  const N = 12;
  for (let i = 0; i < N; i++) {
    const a = i * 2.39996323;                       // golden angle -> evenly scattered azimuths
    const t = 0.5 + ((i * 13) % 7) * 0.085;          // lean 0.5..1.0 rad from vertical (varied)
    const len = 0.72 + ((i * 5) % 4) * 0.07;
    const c = cone(g, 0.05, len, Math.cos(a) * 0.07, 0.16, Math.sin(a) * 0.07, WOOD(0x8a6a3f), 6);
    c.rotation.set(t * Math.sin(a), i * 0.7, -t * Math.cos(a)); // tip leans OUT toward azimuth a
  }
  return g;
}
// Owner-modelled GLB (2026-07-11); the old procedural puck stays as the instant-load fallback.
const MINE_FILE = 'mine.glb';
preloadGLB('/models/' + MINE_FILE);
function buildProceduralMine(g: THREE.Group): void { cyl(g, 0.22, 0.24, 0.1, 0, 0.05, 0, METAL(0x4a4d52, 0.6), 14); cyl(g, 0.12, 0.12, 0.06, 0, 0.13, 0, mat(0xc24a3a, 0.6, 0.3), 12); for (const a of [0, 1.57, 3.14, 4.71]) rivet(g, Math.cos(a) * 0.2, 0.06, Math.sin(a) * 0.2); }
function makeMine(): THREE.Group { return makeGLBBuildable(MINE_FILE, 0.5, buildProceduralMine); }

// --- wall / ceiling furnishings ----------------------------------------------------------------------
// All modelled with the mount plane at local z=0 facing +z (the mount system yaws +z out of the wall),
// origin at the item's centre height so the FOOT half-extents in deployables.ts line up.
// Owner-modelled GLB (2026-07-10) replacing the procedural torch sconce below (kept as the instant-load
// fallback). Wall-mounted, so it fits via fitGLBWallMount like shelf.glb above.
const WALL_TORCH_FILE = 'wall_torch.glb';
preloadGLB('/models/' + WALL_TORCH_FILE);
function makeTorchSconce(): THREE.Group { return makeGLBBuildable(WALL_TORCH_FILE, 1, buildProceduralTorchSconce, fitGLBWallMount); }

function buildProceduralTorchSconce(g: THREE.Group): void {
  box(g, 0.16, 0.3, 0.05, 0, 0.0, 0.02, DARKM());                                     // wall bracket plate
  rivet(g, 0, 0.1, 0.05); rivet(g, 0, -0.1, 0.05);
  const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.03, 0.34, 8), DARKM());
  arm.position.set(0, 0.02, 0.14); arm.rotation.x = 0.6; arm.castShadow = true; g.add(arm); // angled holder
  cyl(g, 0.035, 0.05, 0.3, 0, 0.16, 0.24, WOOD(0x6b4a28), 8);                          // torch shaft
  box(g, 0.09, 0.1, 0.09, 0, 0.34, 0.24, mat(0x3a2c1c, 0.9, 0));                        // wrapped head
  cone(g, 0.06, 0.14, 0, 0.46, 0.24, glow(0xffa030), 7);                                // flame
}
const SHELF_FILE = 'shelf.glb';
preloadGLB('/models/' + SHELF_FILE);
function makeShelf(): THREE.Group { return makeGLBBuildable(SHELF_FILE, 1, buildProceduralShelf, fitGLBWallMount); }
function buildProceduralShelf(g: THREE.Group): void {
  for (const yy of [-0.18, 0.18]) box(g, 1.1, 0.05, 0.3, 0, yy, 0.16, WOOD(0x7a5230));  // two boards
  for (const sx of [-1, 1]) { box(g, 0.05, 0.5, 0.05, sx * 0.48, 0, 0.03, DARKM()); box(g, 0.04, 0.04, 0.26, sx * 0.48, -0.16, 0.15, DARKM()); } // wall rails + brackets
  box(g, 0.2, 0.14, 0.16, -0.25, -0.08, 0.16, mat(0x6a5a3a, 0.85, 0));                  // clutter: a box
  cyl(g, 0.05, 0.05, 0.12, 0.3, 0.28, 0.16, mat(0x4a5234, 0.6, 0.2), 8);                // clutter: a can
}
function makeWoodSign(): THREE.Group {
  const g = new THREE.Group();
  box(g, 0.9, 0.5, 0.05, 0, 0, 0.03, WOOD(0x7a5230));                                   // board
  for (const yy of [-0.2, 0.2]) box(g, 0.94, 0.05, 0.06, 0, yy, 0.03, WOOD(0x5f4827));  // frame rails
  for (const sx of [-1, 1]) rivet(g, sx * 0.4, 0, 0.07);
  box(g, 0.6, 0.04, 0.015, 0, 0.06, 0.06, mat(0x3a2c1c, 0.9, 0));                        // carved line marks
  box(g, 0.44, 0.04, 0.015, -0.08, -0.06, 0.06, mat(0x3a2c1c, 0.9, 0));
  return g;
}
function makeHangingLantern(): THREE.Group {
  const g = new THREE.Group();
  // hangs BELOW its origin (the mount system stands it half-height off the ceiling underside)
  for (const yy of [0.34, 0.26, 0.18]) tube(g, 0.012, 0.05, 0, yy, 0, DARKM(), 'x', 6);  // chain links
  box(g, 0.16, 0.05, 0.16, 0, 0.12, 0, DARKM());                                          // cap
  box(g, 0.12, 0.2, 0.12, 0, 0.0, 0, glow(0xffb84a));                                     // glass body (lit)
  for (const sx of [-1, 1]) box(g, 0.02, 0.22, 0.02, sx * 0.06, 0.0, 0.06, DARKM());      // cage bars
  box(g, 0.14, 0.04, 0.14, 0, -0.12, 0, DARKM());                                         // base dish
  return g;
}

// --- farming --------------------------------------------------------------------------------------
// A planked soil trough. Origin at base centre on y=0; the SOIL TOP sits at local y=0.34 -- farming.ts
// parents its staged crop models there (PLANTER_SOIL_Y), so the two files must agree on that height.
export const PLANTER_SOIL_Y = 0.34;
function makePlanter(): THREE.Group {
  const g = new THREE.Group();
  box(g, 1.2, 0.3, 0.06, 0, 0.21, 0.37, WOOD(0x7a5230)); box(g, 1.2, 0.3, 0.06, 0, 0.21, -0.37, WOOD(0x6b4a28)); // long plank sides
  box(g, 0.06, 0.3, 0.68, 0.57, 0.21, 0, WOOD(0x6b4a28)); box(g, 0.06, 0.3, 0.68, -0.57, 0.21, 0, WOOD(0x7a5230)); // end planks
  box(g, 1.14, 0.06, 0.68, 0, 0.06, 0, WOOD(0x5f4827));                                  // floor board (visible until soil covers it)
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.09, 0.42, 0.09, sx * 0.57, 0.21, sz * 0.37, WOOD(0x5f4827)); // corner posts
  box(g, 1.1, 0.24, 0.64, 0, 0.22, 0, mat(0x3a2c1c, 0.98, 0));                            // soil bed, top at PLANTER_SOIL_Y
  for (const [rx, rz] of [[-0.3, 0.14], [0.22, -0.18], [0.42, 0.2]] as const) box(g, 0.07, 0.03, 0.06, rx, PLANTER_SOIL_Y + 0.01, rz, STONE(0x6a6d74)); // scattered pebbles on the soil
  return g;
}

export function buildCraftableModel(id: CraftableId): THREE.Group {
  switch (id) {
    case 'baseclaim': return makeBaseclaim();
    case 'bench1': return makeBench1();
    case 'bench2': return makeBench2();
    case 'bench3': return makeBench3();
    case 'research_table': return makeResearch();
    case 'repair_table': return makeRepair();
    case 'furnace': return makeFurnace();
    case 'large_furnace': return makeLargeFurnace();
    case 'oil_refinery': return makeRefinery();
    case 'sleeping_bag': return makeSleepingBag();
    case 'bed': return makeBed();
    case 'fridge': return makeFridge();
    case 'locker': return makeLocker();
    case 'drop_box': return makeDropBox();
    case 'wooden_door': return makeWoodDoor();
    case 'metal_door': return makeMetalDoor();
    case 'metal_double_door': return makeMetalDoubleDoor();
    case 'reinforced_door': return makeReinforcedDoor();
    case 'wood_window': return makeWoodWindow();
    case 'metal_window': return makeMetalWindow();
    case 'glass_window': return makeGlassWindow();
    case 'reinforced_window': return makeReinforcedWindow();
    case 'water_catcher': return makeWaterCatcher();
    case 'large_water_catcher': return makeLargeWaterCatcher();
    case 'wooden_lock': return makeWoodenLock();
    case 'keypad_lock': return makeKeypadLock();
    case 'pistol_trap': return makePistolTrap();
    case 'shotgun_trap': return makeShotgunTrap();
    case 'flame_trap': return makeFlameTrap();
    case 'spikes': return makeSpikes();
    case 'mine': return makeMine();
    case 'torch_sconce': return makeTorchSconce();
    case 'shelf': return makeShelf();
    case 'wood_sign': return makeWoodSign();
    case 'hanging_lantern': return makeHangingLantern();
    case 'planter': return makePlanter();
  }
}
